import { useCallback, useEffect, useMemo, useRef, useState, type AnchorHTMLAttributes, type MouseEvent } from 'react'
import { format } from 'date-fns'
import { useReducedMotion } from 'framer-motion'
import { Mic } from 'lucide-react'
import { MeetingIcon, useUser } from '@renderer/chat'
import type { MeetingAppName } from '@highlight/common'
import { CalendarEventIconView } from '@renderer/components/Icons/CalendarEventIcon'
import { useCalendars } from '@renderer/hooks/use-calendars'
import { useUpdateAudioNoteTitle } from '@renderer/hooks/audio-notes'
import { Participants } from '@renderer/overlay/components/participants'
import { EditableTitle } from '../../../components/editable-title'
import { KNOWN_MEETING_APPS, formatTimeRange, formatTimeUntil } from '../../../utils/activity-helpers'
import { useJoinMeeting } from '../../../components/meeting/use-join-meeting'
import { useMeetingBriefBody } from '../use-meeting-brief-body'
import { GET_READY_EXIT_SECONDS } from './body'
import { MeetingJoinButton } from './meeting-join-button'
import type { MeetingViewModel } from '../use-meeting-view-model'

function formatRelativeAgo(date: Date): string {
  const diffMs = Date.now() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays < 1) return 'Today'
  if (diffDays === 1) return '1 day ago'
  if (diffDays < 7) return `${diffDays} days ago`

  const diffWeeks = Math.floor(diffDays / 7)
  if (diffWeeks === 1) return '1 week ago'
  if (diffWeeks < 4) return `${diffWeeks} weeks ago`

  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths === 1) return '1 month ago'
  return `${diffMonths} months ago`
}

interface MeetingViewHeaderProps {
  model: MeetingViewModel
}

/**
 * Header meeting card: icon + title, time range with relative time, and
 * participant avatars. Custom Meetings render the same card minus
 * calendar-derived details.
 *
 * The calendar event link moves with what the title is for: an event-only
 * entry has nothing but the event, so its title is the link. Once a note
 * exists the title belongs to the note (and may be renamed), so the icon
 * carries the link instead.
 */
export function MeetingViewHeader({ model }: MeetingViewHeaderProps) {
  const { calendars } = useCalendars()
  const { calendarEvent, audioNote, variant, title, startedAt, endedAt } = model
  const { presentation: briefPresentation } = useMeetingBriefBody(model)
  const { joinLink, joinLabel, isAwaitingJoin, handleJoinClick } = useJoinMeeting(
    calendarEvent,
    'sidepanel meeting view',
  )
  // Join is offered while the meeting is still joinable: no live recording and
  // no note (either means the user is, or was, already in the call), until the
  // scheduled end passes. Deliberately not the latched
  // `isUpcomingWithoutAudioNote` — a happening-now cell drills into this view,
  // so an already-started meeting still needs its Join CTA.
  const isMeetingOver = !!endedAt && endedAt.getTime() < Date.now()
  const showJoinButton = !!joinLink && variant !== 'live' && !model.audioNoteId && !isMeetingOver

  // Was the last-painted frame the settled Get Ready hero? Read on the render
  // where a brief lands (presentation leaves 'get-ready') to play the Join
  // button's slide-up entrance — mirroring the hero's exit gate in body.tsx, so
  // a brief fetch resolving on view open swaps instantly instead.
  const heroWasSettled = useRef(false)
  useEffect(() => {
    heroWasSettled.current = briefPresentation === 'get-ready' && !model.briefLoading
  })

  // The CTA row stays out of layout while the hero's exit fade plays —
  // mounting it at the flip grows the header and shoves the still-fading hero
  // downward. Latched during render (the ref is only true on the flip's first
  // render), released in sync with the hero's fade, then the row enters with
  // the Join fade-up. Reduced motion skips the hold with the rest of the
  // sequence.
  const shouldReduceMotion = useReducedMotion()
  const [ctaEntrance, setCtaEntrance] = useState<'none' | 'held' | 'entering'>('none')
  if (briefPresentation !== 'get-ready' && heroWasSettled.current && !shouldReduceMotion && ctaEntrance === 'none') {
    setCtaEntrance('held')
  }
  useEffect(() => {
    if (ctaEntrance !== 'held') {
      return
    }
    const id = setTimeout(() => setCtaEntrance('entering'), GET_READY_EXIT_SECONDS * 1000)
    return () => clearTimeout(id)
  }, [ctaEntrance])

  const participants = useMemo(
    () => (calendarEvent ? calendarEvent.attendees.map((attendee) => attendee.email) : []),
    [calendarEvent],
  )

  const icon = useMemo(() => {
    if (calendarEvent) {
      return <CalendarEventIconView event={calendarEvent} size={16} calendars={calendars} />
    }
    const appName = audioNote?.meeting?.app?.name
    if (appName && KNOWN_MEETING_APPS.includes(appName)) {
      return <MeetingIcon appName={appName as MeetingAppName} size={16} />
    }
    return <Mic className="text-foreground-secondary size-4" />
  }, [calendarEvent, calendars, audioNote?.meeting?.app?.name])

  const calendarEventLink = calendarEvent?.calendarEventLink

  // The audio-notes PUT is scoped to the note's owner, so a note shared with
  // this user rejects the write. Local notes carry no userId and have no other
  // owner to belong to.
  const user = useUser()
  const canRenameNote = variant === 'past' && !!audioNote && (!audioNote.userId || audioNote.userId === user?.id)
  const { mutateAsync: updateNoteTitle } = useUpdateAudioNoteTitle()
  const audioNoteId = audioNote?.id
  const handleSaveTitle = useMemo(() => {
    if (!canRenameNote || !audioNoteId) {
      return undefined
    }
    return async (nextTitle: string) => {
      await updateNoteTitle({ id: audioNoteId, title: nextTitle })
    }
  }, [canRenameNote, audioNoteId, updateNoteTitle])

  // Plain left-click rewrites Google URLs to include `authuser` then opens in
  // a new tab; modifier-clicks fall through to the anchor's default behavior.
  const handleCalendarLinkClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      if (!calendarEventLink) return
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
      e.preventDefault()
      try {
        const url = new URL(calendarEventLink)
        if (calendarEvent?.userEmail && url.hostname.includes('google.com')) {
          url.searchParams.set('authuser', calendarEvent.userEmail)
        }
        window.open(url.toString(), '_blank', 'noopener,noreferrer')
      } catch {
        window.open(calendarEventLink, '_blank', 'noopener,noreferrer')
      }
    },
    [calendarEventLink, calendarEvent?.userEmail],
  )

  const timeLine = useMemo(() => {
    if (!startedAt) return null
    const range = calendarEvent
      ? formatTimeRange(new Date(calendarEvent.start), calendarEvent.end ? new Date(calendarEvent.end) : undefined)
      : format(startedAt, 'h:mmaaa')

    let relative: string
    if (variant === 'live') {
      relative = 'now'
    } else if (endedAt && endedAt.getTime() < Date.now()) {
      relative = formatRelativeAgo(endedAt)
    } else {
      relative = formatTimeUntil(startedAt, endedAt ?? undefined)
    }
    return { range, relative }
  }, [calendarEvent, startedAt, endedAt, variant])

  return (
    <div data-tour-section="meeting-header" className="flex shrink-0 flex-col gap-1.5 px-6 py-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {audioNote && calendarEventLink ? (
            <CalendarEventLink
              href={calendarEventLink}
              onClick={handleCalendarLinkClick}
              aria-label="Open calendar event"
              className="hover:bg-element-hover corner-squircle flex size-5 shrink-0 items-center justify-center rounded-[var(--radius-size-xs)] transition-colors duration-150"
            >
              {icon}
            </CalendarEventLink>
          ) : (
            <span className="flex size-5 shrink-0 items-center justify-center">{icon}</span>
          )}
          {audioNote ? (
            <EditableTitle title={title} onSave={handleSaveTitle} />
          ) : (
            <h2 className="text-foreground type-ui-sm truncate font-medium">
              {calendarEventLink ? (
                <CalendarEventLink
                  href={calendarEventLink}
                  onClick={handleCalendarLinkClick}
                  className="hover:decoration-foreground-secondary underline decoration-transparent transition-[text-decoration-color] duration-150"
                >
                  {title}
                </CalendarEventLink>
              ) : (
                title
              )}
            </h2>
          )}
        </div>
        {participants.length > 0 && (
          <div className="shrink-0">
            <Participants participants={participants} />
          </div>
        )}
      </div>

      {timeLine && (
        <p className="text-foreground-secondary type-ui-xs pl-7 font-medium">
          {timeLine.range}
          {' · '}
          {timeLine.relative}
        </p>
      )}

      {/* Not-yet-in-the-call CTA. Asking for a brief isn't offered here: the
          Brief panel hosts that prompt in its empty state, and it is on screen
          in every state this card could have shown it — tabbed, or untabbed
          with no brief to render instead. Suppressed for `'get-ready'`, where
          Join lives in the hero. */}
      {briefPresentation !== 'get-ready' && ctaEntrance !== 'held' && showJoinButton && (
        <div className="mt-2">
          <MeetingJoinButton
            joinLabel={joinLabel}
            isAwaitingJoin={isAwaitingJoin}
            onClick={handleJoinClick}
            animateIn={ctaEntrance === 'entering'}
          />
        </div>
      )}
    </div>
  )
}

function CalendarEventLink({ children, ...props }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  )
}
